<template>
  <a-form
    :key="prefilledForm_reRenderKey"
    :is-term-accepted.sync="isTermAccepted"
    :captcha-key.sync="captchaKey"
    :form-type="$options.consts.FORM_TYPE.CONTACT_US"
    :in-progress="inProgress"
    :hide-title="hideTitle"
    :hide-description="hideDescription"
    :form-validation-fn="formValidationFn"
    :errors="$v"
    :scroll-to-success-message="scrollToSuccessMessage"
    :external-settings="externalSettings"
    class="contact-form"
    ref="form"
    :success-message-modal="successMessageModal"
    :form-id="formId"
    @submit="submitForm"
  >
    <div class="form__row">
      <a-multiselect
        v-model="userForm.DepartmentType"
        :error="$v.userForm.DepartmentType"
        :options="$options.consts.DEPARTMENT_OPTIONS"
        :show-no-results="false"
        :searchable="false"
        :placeholder="departmentPlaceholder"
        label="Department"
        class="contact-form__multiselect"
        :with-mobile-layout="false"
      />
    </div>
    <div class="form__row">
      <a-text-input
        :focusOnMount="focusOnMount"
        type="text"
        v-model="userForm.FirstName"
        :error="$v.userForm.FirstName"
        placeholder="First name"
        prefilled-mode
      />
      <a-text-input
        type="text"
        v-model="userForm.LastName"
        :error="$v.userForm.LastName"
        placeholder="Last name"
        prefilled-mode
      />
    </div>
    <div class="form__row">
      <a-text-input
        type="email"
        v-model="userForm.Email"
        :error="$v.userForm.Email"
        placeholder="Email"
        required
        prefilled-mode
      />
    </div>
    <div class="form__row">
      <a-text-input
        type="tel"
        v-model="userForm.Phone"
        :error="$v.userForm.Phone"
        class="contact-form__phone-field"
        placeholder="Phone"
        prefilled-mode
      />
      <a-text-input
        type="text"
        v-model="userForm.CompanyName"
        :error="$v.userForm.CompanyName"
        placeholder="Company name"
      />
    </div>
    <div class="form__row">
      <a-text-input
        multiline
        v-model="userForm.Message"
        :error="$v.userForm.Message"
        placeholder="Message"
      />
    </div>
  </a-form>
</template>

<script>
import { pathOr, prop } from 'ramda'
import { mapActions } from 'vuex'
import { required, email, maxLength } from 'vuelidate/lib/validators'

import { propValidator, PROP_TYPES, phone } from '@fmpedia/validators'
import AMultiselect from '@/components/_shared/AMultiselect'
import AForm, { FORM_TYPE } from 'shared/AForm'
import mixins from '@/utils/mixins'
import { MODAL } from 'modals/AModalWrapper'
import { hydrationHelpers } from '@/utils/mixins/hydrationHelpers'

const DEPARTMENT_TYPE_FIELD = 'DepartmentType'

const DEPARTMENT = {
  NEWSROOM: 'Newsroom',
  ADVERTISE: 'Advertise',
  SUPPORT: 'Support'
}

const DEPARTMENT_OPTIONS = Object.values(DEPARTMENT)

const DEPARTMENTS_TO_SEND_TO_MONDAY = [DEPARTMENT.ADVERTISE]

export default {
  name: 'AContactUsForm',
  mixins: [mixins.prefilledForm(FORM_TYPE.CONTACT_US), hydrationHelpers],
  components: {
    AMultiselect,
    AForm
  },
  props: {
    closeOnSubmit: propValidator([PROP_TYPES.FUNCTION], false, () => () => {}),
    hideTitle: propValidator([PROP_TYPES.BOOLEAN], false, false),
    hideDescription: propValidator([PROP_TYPES.BOOLEAN], false, false),
    focusOnMount: propValidator([PROP_TYPES.BOOLEAN], false, false),
    scrollToSuccessMessage: propValidator([PROP_TYPES.BOOLEAN], false, false),
    showSuccessMessageInModal: propValidator([PROP_TYPES.BOOLEAN], false, true),
    externalSettings: propValidator([PROP_TYPES.OBJECT], false, null),
    formId: propValidator([PROP_TYPES.STRING])
  },
  watch: {
    '$route.query'(nevQuery) {
      this.userForm = this.getUserForm(
        this.$_prefilledForm_getPrefilledModel(),
        nevQuery
      )
    }
  },
  consts: {
    FORM_TYPE,
    DEPARTMENT_OPTIONS,
    resetComponentDataFields: [
      'userForm',
      'captchaKey',
      'isTermAccepted',
      'inProgress'
    ]
  },
  data() {
    return {
      userForm: this.getUserForm(
        this.$_prefilledForm_getPrefilledModel(),
        this.$route.query
      ),
      captchaKey: '',
      isTermAccepted: false,
      inProgress: false
    }
  },
  validations: {
    userForm: {
      FirstName: {
        'maxLength(100)': maxLength(100)
      },
      LastName: {
        'maxLength(100)': maxLength(100)
      },
      Phone: {
        phone
      },
      Email: {
        required,
        email,
        'maxLength(300)': maxLength(300)
      },
      CompanyName: {
        'maxLength(50)': maxLength(50)
      },
      DepartmentType: {
        required
      },
      Message: {
        'maxLength(1000)': maxLength(1000)
      }
    },
    isTermAccepted: {
      acceptTerms: val => val
    }
  },
  computed: {
    departmentPlaceholder() {
      return this.$_hydrationHelpers_mobileOrDesktopValue(
        'Department to contact?*',
        'Which department do you want to contact?*'
      )
    },
    successMessageModal() {
      return this.showSuccessMessageInModal ? MODAL.SUCCESS_CONTACT_US : ''
    }
  },
  methods: {
    pathOr,
    ...mapActions({
      sendLeadDataToMonday: 'monday/sendLeadData',
      sendContactUsLead: 'leads/sendContactUsLead',
      requestCurrentCountryInfo: 'countries/requestCurrentCountryInfo'
    }),
    clearData() {
      this.$helper.resetComponentData(
        this,
        this.$options.consts.resetComponentDataFields
      )
      this.$v.$reset()
    },
    generatePayload() {
      const payload = { ...this.userForm }
      payload.Phone = this.$helper.formatPhone(payload.Phone) || null
      return payload
    },
    async getCurrentCountry() {
      try {
        const { Name } = await this.requestCurrentCountryInfo()
        return Name
      } catch (err) {
        return null
      }
    },
    async generateMondayPayload() {
      const { Phone, ...restFields } = this.generatePayload()
      const Country = await this.getCurrentCountry()

      return {
        ...restFields,
        Phone: this.$helper.removeSpaces(this.userForm.Phone),
        Country,
        Website: this.$env.IL_DOMAIN_URL,
        FormName: 'Contact us',
        Date: new Date().toISOString()
      }
    },
    generateMondayItemName({ FirstName, LastName, Email }) {
      const fullName = [FirstName, LastName].filter(v => !!v).join(' ')

      return fullName ? `${fullName} (${Email})` : Email
    },
    formValidationFn() {
      return !this.$helper.isValidationFailed(this, {
        scrollInContainer: '.a-scrollable-content__scroll-wrapper'
      })
    },
    async submitForm() {
      try {
        this.inProgress = true
        const payload = this.generatePayload()
        await this.sendContactUsLead({
          captchaKey: this.captchaKey,
          payload
        })

        const mondayPayload = await this.generateMondayPayload()
        await this.sendFormToMonday(mondayPayload)
        // TODO pending client's decision regarding success message flow
        if (this.showSuccessMessageInModal) {
          await this.$refs.form.showSuccessMessageInModal()
        } else {
          await this.$refs.form.showSuccessMessage()
        }

        this.clearData()
        this.closeOnSubmit()
      } catch (err) {
        this.$errorHandler(err, this)
      } finally {
        this.inProgress = false
      }
    },
    getUserForm(preFilledModel, selectedOption) {
      return this.validateSelectedOption(selectedOption)
        ? this.getPreFilledModelWithSelectedOption(
            preFilledModel,
            selectedOption
          )
        : preFilledModel
    },
    getPreFilledModelWithSelectedOption(preFilledModel, selectedOption) {
      return { ...preFilledModel, ...selectedOption }
    },
    validateSelectedOption(selectedOption) {
      return DEPARTMENT_OPTIONS.includes(
        prop(DEPARTMENT_TYPE_FIELD, selectedOption)
      )
    },
    async sendFormToMonday(data) {
      const { DepartmentType } = data
      if (!DEPARTMENTS_TO_SEND_TO_MONDAY.includes(DepartmentType)) return

      try {
        const itemName = this.generateMondayItemName(data)

        await this.sendLeadDataToMonday({
          payload: {
            itemName,
            data
          }
        })
      } catch (err) {
        this.$errorHandler(err, this)
      }
    }
  }
}
</script>

<style lang="scss">
.contact-form.form {
  @include mobile {
    margin-bottom: 20px;
  }

  .form__row .contact-form__phone-field {
    .input-group__error {
      width: 205%;

      @include mobile {
        width: 100%;
      }
    }
  }
}
</style>
